import React, {
    useEffect, useRef, FC, MouseEvent, KeyboardEvent,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Path } from 'routes';
import { UserStatus } from 'config/constants';
import { useApp } from 'context/AppContext';
import { getSafeString } from 'utils/getSafeString';
import { ReactComponent as IconDialogs } from 'assets/icons/menu-dialogs.svg';
import { ReactComponent as IconGallery } from 'assets/icons/menu-gallery.svg';
import { ReactComponent as IconSettings } from 'assets/icons/menu-settings.svg';
import { ReactComponent as IconFeedback } from 'assets/icons/menu-feedback.svg';
import { ReactComponent as IconBlockList } from 'assets/icons/menu-blocklist.svg';
import { ReactComponent as IconMain } from 'assets/icons/menu-main.svg';
import { ReactComponent as IconFAQ } from 'assets/icons/menu-faq.svg';
import { ReactComponent as IconLogin } from 'assets/icons/menu-login.svg';
import { ReactComponent as IconHobbies } from 'assets/icons/menu-hobbies.svg';
import { ReactComponent as IconTests } from 'assets/icons/menu-test.svg';
import { ReactComponent as IconNew } from 'assets/icons/menu-new.svg';
import {
    Nav,
    List,
    ProfileLinkWrapper,
    ProfileData,
    Avatar,
    Overflow,
    Item,
    Note,
    LegalLinkWrapper, LegalLinkItem,
} from './Menu.styled';

export const Menu:FC = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const {
        menu: { active, toggle }, userStatus, userProfile, flowUrl,
    } = useApp();

    const menuRefElement = useRef<HTMLDivElement | null>(null);

    const isAuthorized = (userStatus === UserStatus.Authorized);
    const isShowMessageCount = userProfile.unread_messages_count > 0;

    const name = getSafeString(userProfile?.name);
    const avatar = getSafeString(userProfile?.avatar);
    const bio = getSafeString(userProfile?.bio);
    const phone = getSafeString(userProfile?.msisdn).replace(/(\d{3})(\d{2})(\d{3})(\d{2})(\d{2})/, '+$1 $2 $3-$4-$5');

    const closeMenu = (e:MouseEvent | KeyboardEvent) => {
        if ((e.target as HTMLElement).nodeName !== 'NAV' || (e as KeyboardEvent).key === 'Escape') {
            toggle();
        }
    };

    const PrivateMenuPoints = (
        <>
            <Item id="menu-point-dialogs" isActive={pathname === Path.private.dialogs}>
                <Link to={Path.private.dialogs}>
                    <IconDialogs name="option" />
                    <span>{ t('nav.dialogs') }</span>
                    {
                        isShowMessageCount
                    && <Note>{ userProfile.unread_messages_count }</Note>
                    }
                </Link>
            </Item>
            <Item id="menu-point-gallery" isActive={pathname === Path.private.gallery}>
                <Link to={Path.private.gallery}>
                    <IconGallery name="option" />
                    <span>{ t('nav.gallery') }</span>
                </Link>
            </Item>
            <Item id="menu-point-test" isActive={pathname === Path.private.tests}>
                <Link to={Path.private.tests}>
                    <IconTests name="option" />
                    <span>{ t('nav.tests') }</span>
                    <IconNew />
                </Link>
            </Item>
            <Item id="menu-point-settings" isActive={pathname === Path.private.settings}>
                <Link to={Path.private.settings}>
                    <IconSettings name="option" />
                    <span>{ t('nav.settings') }</span>
                </Link>
            </Item>
            <Item id="menu-point-blocklist" isActive={pathname === Path.private.blocklist}>
                <Link to={Path.private.blocklist}>
                    <IconBlockList name="option" />
                    <span>{ t('nav.blocklist') }</span>
                </Link>
            </Item>
            <Item id="menu-point-hobbies" isActive={pathname === Path.private.hobbies}>
                <Link to={Path.private.hobbies}>
                    <IconHobbies name="option" />
                    <span>{ t('nav.hobbies') }</span>
                </Link>
            </Item>
        </>
    );

    const PublicMenuPoints = (
        <>
            <Item id="menu-point-feedback" isActive={pathname === Path.public.feedback}>
                <Link to={Path.public.feedback}>
                    <IconFeedback name="option" />
                    <span>{ t('nav.feedback') }</span>
                </Link>
            </Item>
            <Item id="menu-point-faq" isActive={pathname === Path.public.faq}>
                <Link to={Path.public.faq}>
                    <IconFAQ name="option" />
                    <span>{ t('nav.faq') }</span>
                </Link>
            </Item>
        </>
    );

    const LegalMenuPoints = (
        <LegalLinkWrapper>
            <LegalLinkItem id="menu-point-offer">
                <Link to={Path.public.offer}>
                    <span>{ t('nav.offer') }</span>
                </Link>
            </LegalLinkItem>
            <LegalLinkItem id="menu-point-cookies">
                <Link to={Path.public.cookies}>
                    <span>{ t('nav.cookies') }</span>
                </Link>
            </LegalLinkItem>
            <LegalLinkItem id="menu-point-privacypolicy">
                <Link to={Path.public.policy}>
                    <span>{ t('nav.privacypolicy') }</span>
                </Link>
            </LegalLinkItem>
        </LegalLinkWrapper>
    );

    const ProfileLink = (
        <ProfileLinkWrapper>
            <Link to={Path.private.profile}>
                <Avatar src={avatar} alt={name} />
                <ProfileData>
                    <h6>{ name }</h6>
                    <p>{ bio }</p>
                    <p>{ phone }</p>
                </ProfileData>
            </Link>
        </ProfileLinkWrapper>
    );

    const AuthLink = (
        <Item id="menu-point-auth">
            <a href={flowUrl}>
                <IconLogin name="option" />
                <span>{ t('nav.login') }</span>
            </a>
        </Item>
    );

    useEffect(() => {
        if (menuRefElement.current && active) {
            menuRefElement.current?.focus();
        }
    }, [active, menuRefElement.current]);

    if (pathname === Path.private.registration) return null;

    return (
        <>
            <Nav isActive={active} onClick={closeMenu}>
                { isAuthorized && ProfileLink }
                <Item id="menu-point-main" isActive={pathname === Path.public.main}>
                    <Link to={Path.public.main}>
                        <IconMain name="option" />
                        <span>{ t('nav.main') }</span>
                    </Link>
                </Item>
                <List>
                    { isAuthorized && PrivateMenuPoints }
                    { PublicMenuPoints }
                    { !isAuthorized && AuthLink }
                </List>
                { LegalMenuPoints }
            </Nav>
            <Overflow
                ref={menuRefElement}
                isActive={active}
                tabIndex={active ? 0 : 999}
                onKeyDown={closeMenu}
                onClick={closeMenu}
            />
        </>
    );
};
